// MA-CL-AB: shared age-band primitive.
//
// Extracted from parentPriors.ts (`bandContaining` + private `bandRank`)
// so both the SERVE path (parentPriors.ageCoveredForServeSkillIds) and the
// TREE path (parentPriors.ageCoveredForTreeSkillIds) consume ONE function,
// making it structurally impossible for the two paths to diverge again.
// This is the prerequisite for MA-P0-9 (age-fit live fix).
//
// Exports:
//   ageToBand(age) — numeric age → canonical AgeBand label
//   bandRank(band) — AgeBand → ordinal rank (for comparison/sorting)
//   ageBandLowerEdge(band) — AgeBand → its lower-edge point label ("6.0")
//   studentAgeForBand(band) / canonicalStudentAge(age) — MA-81: the ONE
//     stored studentAge encoding (the band's lower edge, as a number)
//   AgeBand / AgeBandLowerEdge — the types (MA-79: re-exported so the v1
//     engine's learningTypes.AgeBand IS this type, not a second vocabulary)
//
// MA-79 (ADR-106): this is the ONE age→band resolver. The v1 engine's
// truncated resolver (ageBands.ageBandForAge, thresholds x.4/x.9) is deleted
// and placementEngine.placementBandForAge delegates here (lower edge).
//
// NOT exported here: bandForSkill (already in skillMeta.ts); the null-safe
// wrapper ageBandForStudent (stays in parentPriors.ts with its default).

import type { AgeBand } from "@/curriculum/types/ageBandTable";
import { AGE_BAND_ORDER } from "@/curriculum/types/ageBandTable";

export type { AgeBand };

/** The lower edge of a canonical band label, as a typed literal:
 *  "6.0-6.5" → "6.0", "7.5+" → "7.5". DERIVED from the AgeBand union, so it
 *  is the same vocabulary written as a point, not a second one. */
type LowerEdgeOf<B> = B extends `${infer Lo}-${string}`
  ? Lo
  : B extends `${infer Lo}+`
    ? Lo
    : never;
export type AgeBandLowerEdge = LowerEdgeOf<AgeBand>;

/**
 * Map a numeric age (years) to its canonical AgeBand label.
 *
 * Half-open range [lo, hi): a child of exactly 6.0 years maps to "6.0-6.5".
 * The "7.0+" legacy catch-all is SKIPPED — the resolver emits the fine
 * grade-2 bands ("7.0-7.5" / "7.5+") so a 7.x child lands in the band
 * whose skills actually exist.
 *
 * MA-79 (operator ruling D-1001-6): a sub-minimum age (below the first
 * band's lower edge, 4.0) clamps to the YOUNGEST band "4.0-4.5". It used to
 * fall through to "7.5+" — a bug in this primitive (a corrupt 0 or 3.5 age
 * was treated as the oldest child). Unreachable from the app today (App
 * passes Number(age || 6); onboarding and Settings start at 4). Non-finite
 * ages, which callers own (ageBandForStudent maps them to "5.5-6.0"; placement
 * treats them as 6): NaN falls through to "7.5+" and +Infinity matches "7.5+",
 * both exactly as before; -Infinity is below 4.0, so it now clamps to
 * "4.0-4.5" (was "7.5+").
 *
 * Extracted from `bandContaining` in parentPriors.ts. Accepts only finite
 * numbers (null/undefined safety is the caller's concern;
 * ageBandForStudent handles that).
 */
export function ageToBand(ageYears: number): AgeBand {
  const youngest = AGE_BAND_ORDER[0];
  if (ageYears < parseFloat(youngest)) return youngest; // MA-79: sub-minimum → youngest
  for (const band of AGE_BAND_ORDER) {
    if (band === "7.0+") continue; // legacy catch-all skipped; fine bands follow
    if (band.endsWith("+")) {
      if (ageYears >= parseFloat(band)) return band;
    } else {
      const [lo, hi] = band.split("-").map((n) => parseFloat(n));
      if (ageYears >= lo && ageYears < hi) return band;
    }
  }
  return "7.5+"; // only NaN reaches here (unchanged pre-MA-79 value)
}

/** The lower-edge point label of a canonical band ("6.0-6.5" → "6.0",
 *  "7.5+" → "7.5"). Used by placementEngine.placementBandForAge, whose
 *  module ladder is keyed on the lower edge (MA-79). */
export function ageBandLowerEdge(band: AgeBand): AgeBandLowerEdge {
  return (band.endsWith("+") ? band.slice(0, -1) : band.split("-")[0]) as AgeBandLowerEdge;
}

/**
 * MA-81 — the ONE stored `studentAge` encoding for a band: its lower edge as
 * a number ("6.0-6.5" → 6, "6.5-7.0" → 6.5, "7.5+" → 7.5).
 *
 * Every writer of `progress.studentAge` / the registry `age` (onboarding and
 * Parent Settings) writes this value. Onboarding's whole ages 4/5/6/7 already
 * ARE the lower edges of 4.0-4.5 … 7.0-7.5, and the start-module ladder is
 * keyed on the lower edge (placementBandForAge), so this is the existing point
 * of the band, not a new encoding. Settings used to write the band midpoint
 * (6.25); a stored midpoint still reads correctly, because every reader goes
 * through ageToBand (half-open) and a midpoint lies in the same band.
 */
export function studentAgeForBand(band: AgeBand): number {
  return parseFloat(ageBandLowerEdge(band));
}

/**
 * MA-81 — encode any finite age the canonical way: the lower edge of the band
 * ageToBand resolves it to (6 → 6, 6.25 → 6, 6.75 → 6.5). Whole ages 4–7 are
 * fixed points. A non-finite value is returned unchanged (the caller owns
 * null/NaN, exactly as with ageToBand).
 */
export function canonicalStudentAge(ageYears: number): number {
  if (!Number.isFinite(ageYears)) return ageYears;
  return studentAgeForBand(ageToBand(ageYears));
}

/**
 * Ordinal rank of an AgeBand within AGE_BAND_ORDER (curriculum order).
 * Unknown or invalid bands sort last (rank === AGE_BAND_ORDER.length).
 *
 * Extracted verbatim from the private `bandRank` in parentPriors.ts —
 * behavior is byte-identical.
 */
export function bandRank(band: AgeBand | string): number {
  const r = (AGE_BAND_ORDER as readonly string[]).indexOf(band);
  return r < 0 ? AGE_BAND_ORDER.length : r;
}
